import type { CharacterStay, RailLayout } from "@/data/rail-branches";
import {
  emergeOffset,
  markerAppearanceT,
  pickHostId,
} from "../../marker/MarkerEmerge/emerge";
import { markerDepartureT, pickDepartureHostId } from "../../marker/MarkerDepart/depart";
import { groupCenterDx, placeGroupsAt } from "../RailPlaceGroups/groups";
import { markerCircleOffset, type MarkerCircleOffset } from "@/lib/timeline/scene/SceneMotion/motion";
import { stayAt } from "../StayAt/stay";
import { vehicleSeat } from "../RailVehicleRow/row";
import { acquireShift } from "../RailAcquire/touch";
import { vehicleVisitOffset } from "../RailAcquire/visit";

type HostSeat = (id: string, playheadMs: number) => MarkerCircleOffset;

export function seatedOffset(
  characterId: string,
  playheadMs: number,
  rail: RailLayout,
  hostSeat: HostSeat,
): MarkerCircleOffset | null {
  const at = placedOffset(characterId, playheadMs, rail);
  const stay = stayAt(rail.stays, characterId, playheadMs);
  if (at === null || stay === undefined) {
    return null;
  }
  const shift = rail.acquisitions === undefined
    ? null
    : acquireShift(characterId, playheadMs, rail, (id, ms) => placedOffset(id, ms, rail));
  const shifted = shift === null ? at : { dx: at.dx + shift.dx, dy: at.dy + shift.dy };
  const placed = rail.visits === undefined
    ? shifted
    : vehicleVisitOffset(characterId, playheadMs, rail, shifted, (vehicleId, startMs) => (
      vehicleSeat(rail.stays, vehicleId, stay.rootPlaceId, startMs)
    ));
  const leaving = markerDepartureT(rail.stays, characterId, stay, playheadMs);
  if (leaving > 0) {
    const host = departureHost(characterId, stay, rail);
    if (host !== null) {
      return emergeOffset(placed, hostSeat(host, playheadMs), leaving);
    }
  }
  const t = markerAppearanceT(
    rail.stays,
    characterId,
    stay,
    playheadMs,
    rail.minorCharacterIds,
  );
  if (t >= 1) {
    return placed;
  }
  const host = emergenceHost(characterId, stay, rail);
  if (host === null) {
    return placed;
  }
  return emergeOffset(hostSeat(host, playheadMs), placed, t);
}

/** Nobody comes out of, or tucks into, a vehicle, and a vehicle does not tuck into anyone. */
const peopleByStays = new WeakMap<readonly CharacterStay[], readonly CharacterStay[]>();

function peopleOnly(stays: readonly CharacterStay[]): readonly CharacterStay[] {
  let people = peopleByStays.get(stays);
  if (people === undefined) {
    people = stays.filter((stay) => stay.vehicle !== true);
    peopleByStays.set(stays, people);
  }
  return people;
}

export function departureHost(
  characterId: string,
  stay: CharacterStay,
  rail: RailLayout,
): string | null {
  if (stay.vehicle === true) {
    return null;
  }
  return pickDepartureHostId(peopleOnly(rail.stays), characterId, stay, (id) =>
    placedOffset(id, stay.endMs - 1, rail),
  );
}

export function emergenceHost(
  characterId: string,
  stay: CharacterStay,
  rail: RailLayout,
): string | null {
  if (stay.vehicle === true) {
    return null;
  }
  return pickHostId(peopleOnly(rail.stays), characterId, stay, (id) =>
    placedOffset(id, stay.startMs, rail),
  );
}

function placedOffset(
  characterId: string,
  playheadMs: number,
  rail: RailLayout,
): MarkerCircleOffset | null {
  const stay = stayAt(rail.stays, characterId, playheadMs);
  if (stay === undefined) {
    return null;
  }
  if (stay.vehicle === true) {
    return vehicleSeat(rail.stays, characterId, stay.rootPlaceId, playheadMs);
  }
  const groups = placeGroupsAt(rail.stays, stay.rootPlaceId, playheadMs);
  const placeId = stay.placeId ?? stay.rootPlaceId;
  const cohort = groups.find((group) => group.placeId === placeId)?.characterIds
    ?? [];
  const index = Math.max(0, cohort.indexOf(characterId));
  const target = markerCircleOffset(index, cohort.length);
  const shift = groupCenterDx(groups, placeId, false);
  return { dx: target.dx + shift, dy: target.dy };
}
